import { ReactNode, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { LoadingIndicator } from "./components/atoms/LoadingIndicator";
import { Notification } from "./components/atoms/Notification";
import { MaintenancePopupModal } from "./components/organisms/modal/MaintenancePopupModal";
import { PopupModal } from "./components/organisms/modal/PopupModal";
import { ClientDetailPage } from "./components/pages/client_detail";
import { ClientEditPage } from "./components/pages/client_edit";
import { ClientRegistrationPage } from "./components/pages/client_registration";
import { ClientsPage } from "./components/pages/clients";
import { EasyLoginPage } from "./components/pages/easy_login";
import { FileUploadPage } from "./components/pages/file_upload";
import { FileUploadResultPage } from "./components/pages/file_upload_result";
import { ForgotPasswordPage } from "./components/pages/forgot_password";
import { GroupDetailPage } from "./components/pages/group_detail";
import { GroupEditPage } from "./components/pages/group_edit";
import { GroupRegistrationPage } from "./components/pages/group_registration";
import { GroupsPage } from "./components/pages/groups";
import { HomePage } from "./components/pages/home";
import { LoginPage } from "./components/pages/login";
import { LoginPasskeyPage } from "./components/pages/login_passkey";
import { LoginPasswordPage } from "./components/pages/login_password";
import { Maintenance } from "./components/pages/maintenance";
import { PasskeySettingPage } from "./components/pages/passkey_setting";
import { PasskeySettingCompletePage } from "./components/pages/passkey_setting_complete";
import { PasswordSettingPage } from "./components/pages/password_setting";
import { PhoneNumberSettingPage } from "./components/pages/phone_number_setting";
import { ResetPasswordPage } from "./components/pages/reset_password";
import { ResetPasswordCompletePage } from "./components/pages/reset_password_completed";
import { ResetPasswordTimeOutPage } from "./components/pages/reset_password_timeout";
import { SmsRegistrationPage } from "./components/pages/sms_registration";
import { SmsRegistrationCompletePage } from "./components/pages/sms_registration_complete";
import { SmsSendPage } from "./components/pages/sms_send";
import { SmsSendCompletePage } from "./components/pages/sms_send_complete";
import { EmailSendPage } from "./components/pages/email_send";
import { EmailSendCompletePage } from "./components/pages/email_send_complete";
import { EmailRegistrationPage } from "./components/pages/email_registration";
import { EmailRegistrationCompletePage } from "./components/pages/email_registration_complete";
import { UserDetailPage } from "./components/pages/user_detail";
import { UserEditPage } from "./components/pages/user_edit";
import { UserRegistrationPage } from "./components/pages/user_registration";
import { UsersPage } from "./components/pages/users";
import { AuthTemplate } from "./components/templates/auth_template";
import { LoginTemplate } from "./components/templates/LoginTemplate";
import { SideBarTemplate } from "./components/templates/SidebarTemplate";
import { APP_TYPE, AppType } from "./constants/app";
import { PageRoutes } from "./constants/routes";
import { appActions, appSelectors } from "./ducks/app";
import { AppDispatch } from "./store";
import { ForgotPasswordSendMailPage } from "./components/pages/forgot_password_send_mail";

export const RouteConfig: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const isLoading = useSelector(appSelectors.isLoading);

  const showModal = useSelector(appSelectors.showModal);
  const modalObject = useSelector(appSelectors.modalObject);

  const showNotification = useSelector(appSelectors.showNotification);
  const notificationObject = useSelector(appSelectors.notificationObject);

  const isFileUpload = useMemo(() => {
    return APP_TYPE === AppType.FileUpload;
  }, []);

  const isAccountManagement = useMemo(() => {
    return APP_TYPE === AppType.AccountManagement;
  }, []);

  const _getRouteComponent = useCallback(
    (
      route: string,
      component: ReactNode,
      template: "login" | "sidebar",
      settings?: {
        hideLogo?: boolean;
        requiredLogin?: boolean;
        requiredLogout?: boolean;
        requireAdmin?: boolean;
        requireGroupAdmin?: boolean;
      },
    ) => {
      return (
        <Route
          path={route}
          element={
            <>
              {template === "login" && <LoginTemplate {...settings} />}
              {template === "sidebar" && <SideBarTemplate />}
            </>
          }
        >
          <Route
            index
            element={<AuthTemplate {...settings}>{component}</AuthTemplate>}
          ></Route>
        </Route>
      );
    },
    [],
  );

  return (
    <>
      <Routes>
        {_getRouteComponent(PageRoutes.HOME, <HomePage />, "sidebar", {
          requiredLogin: true,
        })}

        {_getRouteComponent(
          PageRoutes.PASSWORD_SETTING,
          <PasswordSettingPage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.PHONE_NUMBER_SETTING,
          <PhoneNumberSettingPage />,
          "login",
          { requiredLogout: true },
        )}

        {_getRouteComponent(PageRoutes.LOGIN, <LoginPage />, "login", {
          requiredLogout: true,
        })}
        {_getRouteComponent(
          PageRoutes.LOGIN_PASSWORD,
          <LoginPasswordPage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.LOGIN_PASSKEY,
          <LoginPasskeyPage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.FORGOT_PASSWORD,
          <ForgotPasswordPage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.FORGOT_PASSWORD_SEND_MAIL,
          <ForgotPasswordSendMailPage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.RESET_PASSWORD,
          <ResetPasswordPage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.RESET_PASSWORD_COMPLETE,
          <ResetPasswordCompletePage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.RESET_PASSWORD_TIMEOUT,
          <ResetPasswordTimeOutPage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(PageRoutes.SMS_SEND, <SmsSendPage />, "login", {
          requiredLogout: true,
        })}
        {_getRouteComponent(
          PageRoutes.SMS_SEND_COMPLETE,
          <SmsSendCompletePage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.SMS_REGISTRATION,
          <SmsRegistrationPage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.SMS_REGISTRATION_COMPLETE,
          <SmsRegistrationCompletePage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(PageRoutes.EMAIL_SEND, <EmailSendPage />, "login", {
          requiredLogout: true,
        })}
        {_getRouteComponent(
          PageRoutes.EMAIL_SEND_COMPLETE,
          <EmailSendCompletePage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.EMAIL_REGISTRATION,
          <EmailRegistrationPage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.EMAIL_REGISTRATION_COMPLETE,
          <EmailRegistrationCompletePage />,
          "login",
          { requiredLogout: true },
        )}
        {_getRouteComponent(PageRoutes.EASY_LOGIN, <EasyLoginPage />, "login", {
          requiredLogout: true,
        })}
        {_getRouteComponent(
          PageRoutes.PASSKEY_SETTING,
          <PasskeySettingPage />,
          "login",
          { hideLogo: true, requiredLogout: true },
        )}
        {_getRouteComponent(
          PageRoutes.PASSKEY_SETTING_COMPLETE,
          <PasskeySettingCompletePage />,
          "login",
          { requiredLogout: true },
        )}

        {isAccountManagement && (
          <>
            {_getRouteComponent(
              PageRoutes.CLIENTS,
              <ClientsPage />,
              "sidebar",
              { requiredLogin: true, requireAdmin: true },
            )}
            {_getRouteComponent(
              PageRoutes.CLIENT_DETAIL,
              <ClientDetailPage />,
              "sidebar",
              { requiredLogin: true, requireAdmin: true },
            )}
            {_getRouteComponent(
              PageRoutes.CLIENT_REGISTRATION,
              <ClientRegistrationPage />,
              "sidebar",
              { requiredLogin: true, requireAdmin: true },
            )}
            {_getRouteComponent(
              PageRoutes.CLIENT_EDIT,
              <ClientEditPage />,
              "sidebar",
              { requiredLogin: true, requireAdmin: true },
            )}
            {_getRouteComponent(PageRoutes.GROUPS, <GroupsPage />, "sidebar", {
              requiredLogin: true,
              requireGroupAdmin: true,
            })}
            {_getRouteComponent(
              PageRoutes.GROUP_DETAIL,
              <GroupDetailPage />,
              "sidebar",
              { requiredLogin: true, requireGroupAdmin: true },
            )}
            {_getRouteComponent(
              PageRoutes.GROUP_REGISTRATION,
              <GroupRegistrationPage />,
              "sidebar",
              { requiredLogin: true, requireAdmin: true },
            )}
            {_getRouteComponent(
              PageRoutes.GROUP_EDIT,
              <GroupEditPage />,
              "sidebar",
              { requiredLogin: true, requireAdmin: true },
            )}
            {_getRouteComponent(PageRoutes.USERS, <UsersPage />, "sidebar", {
              requiredLogin: true,
              requireGroupAdmin: true,
            })}
            {_getRouteComponent(
              PageRoutes.USER_DETAIL,
              <UserDetailPage />,
              "sidebar",
              { requiredLogin: true, requireGroupAdmin: true },
            )}
            {_getRouteComponent(
              PageRoutes.USER_REGISTRATION,
              <UserRegistrationPage />,
              "sidebar",
              { requiredLogin: true, requireGroupAdmin: true },
            )}
            {_getRouteComponent(
              PageRoutes.USER_EDIT,
              <UserEditPage />,
              "sidebar",
              { requiredLogin: true, requireGroupAdmin: true },
            )}
          </>
        )}

        {isFileUpload && (
          <>
            {_getRouteComponent(
              PageRoutes.FILE_UPLOAD,
              <FileUploadPage />,
              "sidebar",
              { requiredLogin: true },
            )}
            {_getRouteComponent(
              PageRoutes.FILE_UPLOAD_RESULT,
              <FileUploadResultPage />,
              "sidebar",
              { requiredLogin: true },
            )}
          </>
        )}
        <Route
          path={PageRoutes.NOT_FOUND}
          element={<Navigate to="/" />}
        ></Route>
        {_getRouteComponent(PageRoutes.MAINTENANCE, <Maintenance />, "login", {
          requiredLogin: false,
          requireGroupAdmin: false,
        })}
      </Routes>

      <LoadingIndicator isShown={isLoading} />
      <PopupModal
        {...modalObject}
        isOpen={showModal}
        defaultCloseModal={() => {
          dispatch(appActions.hideModal());
          modalObject.onDefaultCloseModal?.();
        }}
      />
      <MaintenancePopupModal />
      <Notification
        notification={notificationObject}
        isVisible={showNotification}
      />
    </>
  );
};
